import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { colors, fonts } from "../../common/mixins";
import { formatPrice } from "../../../utils/Utils";
import { QuantitySpinner } from "../../common/QuantitySpinner";
import { useReservationStore } from "../../../stores/useReservationStore";
import type {
  ProductOption,
  StoreProduct,
} from "../../../stores/useReservationStore";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import type { ProductLine } from "../../../pageComponents/reservationPage/utils/productQuantities";
import {
  getScopedMaxQty,
  getScopedMinQty,
  getScopedQty,
} from "../../../pageComponents/reservationPage/utils/productQuantities";
import { BREAKPOINT_RESERVATION_PAGE } from "../../../pageComponents/reservationPage/constants/breakpoints";
import {
  ItemContent,
  ItemMeta,
  ItemPrice,
  ItemRow,
  ItemTitle,
} from "./shared.styles";

// A products list is a genuinely separate concern from the order card, and a
// module-level component (not one declared inside OrderInfo) so its identity
// is stable across renders — otherwise React remounts it on every keystroke
// and its `expanded` state (and the view-all toggle) silently resets.

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  ${fonts.smallDarkGray()};
`;

const ItemContainer = styled.div`
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ViewAllWrapper = styled.div`
  padding-top: 16px;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    padding-top: 8px;
  }
`;

const ViewAll = styled.a`
  line-height: 16px;
  font-family: "pf_highway_sans_pro_lightRg";
  font-size: 16px;
  font-weight: 600;
  color: ${colors.primaryButton};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${colors.buttonHovered};
    text-decoration: none;
  }
  &:active {
    color: ${colors.buttonPressed};
  }

  // CSS approach to reset the color on :focus:not(:focus-visible)
  //  — this catches mouse-click focus (not keyboard focus):
  &:focus:not(:focus-visible) {
    color: ${colors.primaryButton};
    outline: none;
  }
`;

// Italic ticket reference next to a per-ticket row's product name. No explicit
// font-size: it inherits ItemTitle's, since it is always rendered inside one.
const TicketRef = styled.span`
  font-style: italic;
`;

type Resources = ReturnType<typeof useReservationResources>;

/**
 * Spinner floor/ceiling for one cart line, in the line's own scope (a single
 * ticket, or the whole order when `ticketId` is null).
 *
 * Per-order: the floor is the option minimum only while this scope is the
 * last one holding the option — otherwise a row legitimately below the
 * minimum (e.g. an order holding 1 unit against a minimumOrder of 3, already
 * satisfied some other way) would have a − button that raises the quantity
 * instead of lowering it.
 *
 * Per-ticket: the floor is flat — a ticket holds 0 or at least the minimum,
 * there is no "last scope" to reason about.
 */
function getSpinnerBounds(
  product: StoreProduct,
  option: ProductOption,
  ticketId: number | null,
  orderedTicketIds: number[],
) {
  const scopedQty = getScopedQty(product, option.id, ticketId ?? undefined);
  const min = getScopedMinQty(product, option, ticketId ?? undefined);
  const max = getScopedMaxQty(
    product,
    option,
    ticketId ?? undefined,
    orderedTicketIds,
  );
  return { scopedQty, min, max };
}

interface ProductRowProps {
  line: ProductLine;
  orderedTicketIds: number[];
  currency: string;
  isUnder900: boolean;
  ticketNumber?: number;
  r: Resources;
  onIncrease: () => void;
  onDecrease: () => void;
  onDelete: () => void;
}

function ProductRow({
  line,
  orderedTicketIds,
  currency,
  isUnder900,
  ticketNumber,
  r,
  onIncrease,
  onDecrease,
  onDelete,
}: ProductRowProps) {
  const { product, option, qty } = line;
  const price = option.discountedPrice ?? option.price ?? 0;
  const { min, max } = getSpinnerBounds(
    product,
    option,
    line.ticketId,
    orderedTicketIds,
  );

  return (
    <Row id="product-item">
      <ItemRow>
        <ItemContent>
          <ItemContainer>
            <ItemTitle id="product-name">
              {product.name}
              {ticketNumber !== undefined && (
                <TicketRef>
                  {" "}
                  ({r.TicketNumber.replace("{0}", String(ticketNumber))})
                </TicketRef>
              )}
            </ItemTitle>
            {product.productOptions.length > 1 && (
              <ItemMeta id="product-option-name">{option.name}</ItemMeta>
            )}
          </ItemContainer>
        </ItemContent>
        <ItemPrice id="product-price">
          {formatPrice(price * qty, currency)}
        </ItemPrice>
      </ItemRow>
      {!isUnder900 && (
        <QuantitySpinner
          quantity={qty}
          min={min}
          max={max}
          onIncrease={onIncrease}
          onDecrease={onDecrease}
          onDelete={onDelete}
        />
      )}
    </Row>
  );
}

/** Reads/writes a cart line's quantity in whichever map (order or ticket) it belongs to. */
function useProductQuantityActions() {
  const orderedTicketIds = useReservationStore((s) => s.orderedTicketIds);
  const setOptionQuantity = useReservationStore((s) => s.setOptionQuantity);
  const setTicketOptionQuantity = useReservationStore(
    (s) => s.setTicketOptionQuantity,
  );

  const updateQuantity = (
    { product, option, ticketId }: ProductLine,
    delta: number,
  ) => {
    const { scopedQty, min, max } = getSpinnerBounds(
      product,
      option,
      ticketId,
      orderedTicketIds,
    );
    const newQty = Math.min(Math.max(min, scopedQty + delta), max);
    if (ticketId === null) {
      setOptionQuantity(product.id, option.id, newQty);
    } else {
      setTicketOptionQuantity(product.id, ticketId, option.id, newQty);
    }
  };

  const removeQuantity = ({ product, option, ticketId }: ProductLine) => {
    if (ticketId === null) {
      setOptionQuantity(product.id, option.id, 0);
    } else {
      setTicketOptionQuantity(product.id, ticketId, option.id, 0);
    }
  };

  return { updateQuantity, removeQuantity };
}

function ProductRowsList({
  rows,
  orderedTicketIds,
  currency,
  isUnder900,
  ticketNumberMap,
  r,
  updateQuantity,
  removeQuantity,
}: {
  rows: ProductLine[];
  orderedTicketIds: number[];
  currency: string;
  isUnder900: boolean;
  ticketNumberMap?: Map<number, number>;
  r: Resources;
  updateQuantity: (line: ProductLine, delta: number) => void;
  removeQuantity: (line: ProductLine) => void;
}) {
  return (
    <>
      {rows.map((line) => (
        <ProductRow
          key={`${line.product.id}-${line.option.id}-${line.ticketId ?? "order"}`}
          line={line}
          orderedTicketIds={orderedTicketIds}
          currency={currency}
          isUnder900={isUnder900}
          ticketNumber={
            line.ticketId !== null
              ? ticketNumberMap?.get(line.ticketId)
              : undefined
          }
          r={r}
          onIncrease={() => updateQuantity(line, 1)}
          onDecrease={() => updateQuantity(line, -1)}
          onDelete={() => removeQuantity(line)}
        />
      ))}
    </>
  );
}

function ViewAllToggle({
  expanded,
  onToggle,
  moreLabel,
  lessLabel,
  idSuffix,
}: {
  expanded: boolean;
  onToggle: () => void;
  moreLabel: string;
  lessLabel: string;
  idSuffix: string;
}) {
  return (
    <ViewAllWrapper id={`view-all-products-wrapper${idSuffix}`}>
      <ViewAll
        id={`view-all-products-btn${idSuffix}`}
        role="button"
        onClick={onToggle}
      >
        {expanded ? lessLabel : moreLabel}
      </ViewAll>
    </ViewAllWrapper>
  );
}

interface ProductRowsSectionProps {
  rows: ProductLine[];
  title: string;
  isSummaryDrawer: boolean;
  isUnder900: boolean;
  currency: string;
  ticketNumberMap?: Map<number, number>;
  // Rows shown before "view all". Drops to 2 when the order has both kinds of
  // product, so two stacked product sections do not push the total off screen.
  collapsedCount?: number;
  // Appended to this section's DOM ids. The section is rendered twice when an
  // order has both kinds of product; the per-order section keeps the original
  // unsuffixed ids so existing external selectors still resolve to it.
  idSuffix?: string;
}

export function ProductRowsSection({
  rows,
  title,
  isSummaryDrawer,
  isUnder900,
  currency,
  ticketNumberMap,
  collapsedCount = 3,
  idSuffix = "",
}: ProductRowsSectionProps) {
  const r = useReservationResources();
  const orderedTicketIds = useReservationStore((s) => s.orderedTicketIds);
  const { updateQuantity, removeQuantity } = useProductQuantityActions();
  const [expanded, setExpanded] = useState(false);

  const totalQuantity = useMemo(
    () => rows.reduce((sum, line) => sum + line.qty, 0),
    [rows],
  );
  const visibleRows = isSummaryDrawer
    ? rows
    : expanded
      ? rows
      : rows.slice(0, collapsedCount);
  return (
    <>
      <Container id={`products${idSuffix}`}>
        <SectionTitle id={`products-title${idSuffix}`}>
          {title}
          {!isSummaryDrawer && ` (${totalQuantity})`}
        </SectionTitle>
        <ProductRowsList
          rows={visibleRows}
          orderedTicketIds={orderedTicketIds}
          currency={currency}
          isUnder900={isUnder900}
          ticketNumberMap={ticketNumberMap}
          r={r}
          updateQuantity={updateQuantity}
          removeQuantity={removeQuantity}
        />
      </Container>

      {!isSummaryDrawer && rows.length > collapsedCount && (
        <ViewAllToggle
          expanded={expanded}
          onToggle={() => setExpanded((s) => !s)}
          moreLabel={r.ViewAllProducts}
          lessLabel={r.ViewLessProducts}
          idSuffix={idSuffix}
        />
      )}
    </>
  );
}
